import { useEffect, useReducer } from "react";

import { getInvitationExpiryState } from "../utils/invitationExpiry";
import type { InvitationExpiryState } from "../utils/invitationExpiry";

// Browsers overflow timeout delays beyond a signed 32-bit integer.
const MAX_TIMEOUT_MS = 2_147_483_647;

interface InvitationExpiryPresentation {
    now: number;
    state: InvitationExpiryState;
}

/** Keep roster indicators and detail copy current without refetching unchanged API data. */
export function useInvitationExpiry(inviteExpiresAt: string | null | undefined): InvitationExpiryPresentation {
    const [, refresh] = useReducer((revision: number) => revision + 1, 0);
    const now = Date.now();

    useEffect(() => {
        const expiresAt = inviteExpiresAt ? Date.parse(inviteExpiresAt) : Number.NaN;
        if (!Number.isFinite(expiresAt)) return;

        let timer: ReturnType<typeof setTimeout> | undefined;
        const schedule = (): void => {
            clearTimeout(timer);
            const remaining = expiresAt - Date.now();
            if (remaining > 0) timer = setTimeout(update, Math.min(remaining, MAX_TIMEOUT_MS));
        };
        const update = (): void => {
            refresh();
            schedule();
        };
        const onVisibilityChange = (): void => {
            if (document.visibilityState === "visible") update();
        };

        // Expiry may have passed between rendering and running this effect.
        update();
        window.addEventListener("focus", update);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return (): void => {
            clearTimeout(timer);
            window.removeEventListener("focus", update);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [inviteExpiresAt]);

    return { now, state: getInvitationExpiryState(inviteExpiresAt, now) };
}
